import type {
  MpClient,
  MpEvent,
  MpPlayer,
  MpPositionTick,
  MpRoomState,
} from "./types";

/**
 * Same-machine multi-tab transport using BroadcastChannel.
 *
 * Roles:
 * - The first peer to call join({asHost:true}) becomes host.
 * - The host owns authoritative `MpRoomState` (player list, phase, seed).
 * - Other peers send `hello` and apply received `state` snapshots.
 *
 * Position ticks flow peer-to-peer (every peer broadcasts its own tick;
 * other peers consume them as snapshots).
 *
 * Production path: swap this for a `WebSocketTransport` that talks to the
 * Cloudflare `RaceRoom` Durable Object. The MpClient interface is identical
 * so callers don't change.
 */
export class BroadcastTransport implements MpClient {
  private bc: BroadcastChannel | null = null;
  private state: MpRoomState | null = null;
  private myId = "";
  private isHost = false;
  private listeners: Array<(e: MpEvent) => void> = [];

  async join(opts: {
    code: string;
    asHost: boolean;
    me: { id: string; displayName: string; avatarConfig: import("@shared/types").AvatarConfig };
    lessonId?: string;
    seed?: number;
  }): Promise<MpRoomState> {
    this.myId = opts.me.id;
    this.isHost = opts.asHost;
    this.bc = new BroadcastChannel(`wr-room-${opts.code}`);
    this.bc.onmessage = (ev) => this.onMessage(ev.data);

    if (opts.asHost) {
      // Host establishes the room.
      this.state = {
        code: opts.code,
        hostId: this.myId,
        lessonId: opts.lessonId ?? "",
        seed: opts.seed ?? Math.floor(Math.random() * 1e9),
        phase: "lobby",
        players: [
          {
            ...opts.me,
            slotIdx: 0,
            ready: false,
            finished: false,
          },
        ],
      };
      this.broadcastState();
      return this.state;
    }
    // Joiner: ask the host for current state.
    return new Promise<MpRoomState>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.bc?.close();
        this.bc = null;
        reject(new Error("Room not found — check the code or ask the host to open it"));
      }, 1500);
      const off = this.on("event", (e) => {
        if (e.type === "state" && e.state.code === opts.code) {
          clearTimeout(timer);
          off();
          // Add ourselves to host's roster.
          this.bc!.postMessage({
            type: "hello",
            from: this.myId,
            me: opts.me,
          });
          // Wait for host to acknowledge with state including us.
          const off2 = this.on("event", (ev) => {
            if (ev.type === "state" && ev.state.players.some((p) => p.id === this.myId)) {
              off2();
              resolve(ev.state);
            }
          });
        }
      });
      // Probe the channel — host will reply with current state.
      this.bc!.postMessage({ type: "probe", from: this.myId });
    });
  }

  setReady(ready: boolean): void {
    if (!this.bc) return;
    if (this.isHost && this.state) {
      const me = this.state.players.find((p) => p.id === this.myId);
      if (me) me.ready = ready;
      this.broadcastState();
    } else {
      this.bc.postMessage({ type: "set-ready", from: this.myId, ready });
    }
  }

  startRace(): void {
    if (!this.isHost || !this.state) return;
    this.state.phase = "countdown";
    this.state.countdownStartedAt = Date.now();
    this.broadcastState();
    setTimeout(() => {
      if (!this.state) return;
      this.state.phase = "racing";
      this.broadcastState();
    }, 3000);
  }

  sendTick(tick: MpPositionTick): void {
    this.bc?.postMessage({ type: "tick", from: this.myId, tick });
  }

  reportFinish(opts: {
    timeMs: number;
    score: number;
    correctCount: number;
    totalQuestions: number;
  }): void {
    this.bc?.postMessage({
      type: "finish",
      from: this.myId,
      timeMs: opts.timeMs,
      score: opts.score,
      correctCount: opts.correctCount,
      totalQuestions: opts.totalQuestions,
    });
    if (this.isHost) this.applyFinish(this.myId, opts);
  }

  on(_event: "event", cb: (e: MpEvent) => void): () => void {
    this.listeners.push(cb);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== cb);
    };
  }

  leave(): void {
    if (this.bc) {
      this.bc.postMessage({ type: "leave", from: this.myId });
      this.bc.close();
      this.bc = null;
    }
    this.state = null;
    this.listeners = [];
  }

  // ─── internals ────────────────────────────────────────────────────────

  private emit(e: MpEvent) {
    for (const l of this.listeners) l(e);
  }

  private broadcastState() {
    if (!this.state) return;
    this.bc?.postMessage({ type: "state", state: this.state });
    this.emit({ type: "state", state: cloneState(this.state) });
  }

  private onMessage(msg: { type: string; [k: string]: unknown }) {
    if (msg.type === "tick") {
      this.emit({
        type: "tick",
        from: msg.from as string,
        tick: msg.tick as MpPositionTick,
      });
      return;
    }
    if (msg.type === "leave") {
      if (this.isHost && this.state) {
        this.state.players = this.state.players.filter((p) => p.id !== msg.from);
        this.broadcastState();
      }
      return;
    }
    if (this.isHost && this.state) {
      switch (msg.type) {
        case "probe":
          this.broadcastState();
          break;
        case "hello": {
          const me = msg.me as MpPlayer;
          if (!this.state.players.some((p) => p.id === me.id)) {
            const slotIdx = this.state.players.length;
            this.state.players.push({ ...me, slotIdx, ready: false, finished: false });
            this.broadcastState();
          }
          break;
        }
        case "set-ready": {
          const p = this.state.players.find((x) => x.id === msg.from);
          if (p) p.ready = !!msg.ready;
          this.broadcastState();
          break;
        }
        case "finish":
          this.applyFinish(
            msg.from as string,
            msg as unknown as Parameters<MpClient["reportFinish"]>[0],
          );
          break;
      }
    } else {
      // Joiner: only state messages from host matter.
      if (msg.type === "state") {
        this.state = msg.state as MpRoomState;
        this.emit({ type: "state", state: cloneState(this.state) });
      }
    }
  }

  private applyFinish(
    fromId: string,
    f: { timeMs: number; score: number; correctCount: number; totalQuestions: number },
  ) {
    if (!this.state) return;
    const p = this.state.players.find((x) => x.id === fromId);
    if (!p) return;
    p.finished = true;
    p.finishTimeMs = f.timeMs;
    p.finishScore = f.score;
    p.finishCorrect = f.correctCount;
    p.finishTotal = f.totalQuestions;
    // Once all real players finish (bots count as finished from the start
    // when added by host), move to results. Add a 12-second hard timeout too.
    const remaining = this.state.players.filter((q) => !q.finished && !q.isBot).length;
    if (remaining === 0 && this.state.phase === "racing") {
      this.state.phase = "results";
    }
    this.broadcastState();
  }
}

function cloneState(s: MpRoomState): MpRoomState {
  return {
    ...s,
    players: s.players.map((p) => ({ ...p })),
  };
}
